'use client';

import React from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import BigNumber from 'bignumber.js';
import { FiCopy, FiCheck, FiInbox, FiPlus } from 'react-icons/fi';
import styles from './TokenDetailPage.module.css';
import useApiQuery from 'lib/api/useApiQuery';
import useQueryWithPages from 'src/hooks/useQueryWithPages';
import { formatTimestamp } from '../../utils/formatTimestamp';

const TokenDetailPage: React.FC = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const hash = params?.hash as string;
  const tab = searchParams?.get('tab') || 'transfers';
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const tokenQuery = useApiQuery('token', {
    pathParams: { hash },
    queryOptions: { enabled: Boolean(hash) },
  });

  const countersQuery = useApiQuery('token_counters', {
    pathParams: { hash },
    queryOptions: { enabled: Boolean(hash) },
  });

  const transfersQuery = useQueryWithPages({
    resourceName: 'token_transfers',
    pathParams: { hash },
    options: { enabled: Boolean(hash) && tab === 'transfers' },
  });

  const holdersQuery = useQueryWithPages({
    resourceName: 'token_holders',
    pathParams: { hash },
    options: { enabled: Boolean(hash) && tab === 'holders' },
  });

  const token = tokenQuery.data as any;
  const counters = countersQuery.data as any;

  const [addedToWallet, setAddedToWallet] = React.useState(false);

  const handleAddToMetaMask = React.useCallback(async() => {
    const ethereum = (window as any).ethereum;
    if (!ethereum || !token) return;

    try {
      await ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: token.address,
            symbol: token.symbol,
            decimals: Number(token.decimals || 18),
            image: token.icon_url || '',
          },
        },
      });
      setAddedToWallet(true);
      setTimeout(() => setAddedToWallet(false), 3000);
    } catch {
      // user rejected
    }
  }, [token]);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatSupply = (value: string | null | undefined, decimals: string | null | undefined) => {
    if (!value || value === '0') return '0';
    const dec = decimals ? Number(decimals) : 18;
    const bn = BigNumber(value);
    const amount = bn.dividedBy(BigNumber(10).pow(dec));
    if (amount.isGreaterThanOrEqualTo(1000000000)) return `${amount.dividedBy(1000000000).toFixed(2)}B`;
    if (amount.isGreaterThanOrEqualTo(1000000)) return `${amount.dividedBy(1000000).toFixed(2)}M`;
    if (amount.isGreaterThanOrEqualTo(1000)) return `${amount.dividedBy(1000).toFixed(2)}K`;
    return amount.toFixed(4);
  };

  const isLoading = tokenQuery.isLoading;
  const isCountersLoading = countersQuery.isLoading;

  if (!isLoading && !token) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Token not found</div>
      </div>
    );
  }

  const currentQuery = tab === 'holders' ? holdersQuery : transfersQuery;
  const items = currentQuery.data?.items || [];

  const skeleton = (width = '120px') => (
    <span className={styles.skeleton} style={{ width }} />
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.tokenIcon}>
          {!isLoading && token?.icon_url ? (
            <img src={token.icon_url} alt={token.name} />
          ) : (
            isLoading ? '?' : (token?.name?.[0] || '?')
          )}
        </div>
        <h1 className={styles.title}>
          {isLoading ? skeleton('200px') : (
            <>{token.name} <span className={styles.symbol}>({token.symbol})</span></>
          )}
        </h1>
        {!isLoading && <span className={styles.typeBadge}>{token.type}</span>}
        {!isLoading && token?.type === 'ERC-20' && (
          <button className={styles.addToWallet} onClick={handleAddToMetaMask}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg" alt="MetaMask" width={18} height={18} />
            {addedToWallet ? 'Added!' : 'Add to MetaMask'}
          </button>
        )}
      </div>

      <div className={styles.detailCard}>
        <div className={styles.row}>
          <div className={styles.label}>Contract Address</div>
          <div className={styles.value}>
            {isLoading ? skeleton('100%') : (
              <>
                <a href={`/address/${token.address}`} className={styles.link} style={{ fontFamily: 'monospace' }}>
                  {token.address}
                </a>
                <button
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(token.address, 'contract')}
                >
                  {copiedField === 'contract' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Total Supply</div>
          <div className={styles.value}>
            {isLoading ? skeleton('160px') : `${formatSupply(token.total_supply, token.decimals)} ${token.symbol}`}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Holders</div>
          <div className={styles.value}>
            {isLoading ? skeleton('80px') : Number(token.holders || 0).toLocaleString()}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Transfers</div>
          <div className={styles.value}>
            {isCountersLoading ? skeleton('80px') : Number(counters?.transfers_count || 0).toLocaleString()}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Decimals</div>
          <div className={styles.value}>
            {isLoading ? skeleton('40px') : token.decimals}
          </div>
        </div>

        {!isLoading && token?.exchange_rate && (
          <div className={styles.row}>
            <div className={styles.label}>Price</div>
            <div className={styles.value}>${token.exchange_rate}</div>
          </div>
        )}

        {!isLoading && token?.circulating_market_cap && (
          <div className={styles.row}>
            <div className={styles.label}>Market Cap</div>
            <div className={styles.value}>${Number(token.circulating_market_cap).toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <a
          href={`/token/${hash}?tab=transfers`}
          className={`${styles.tab} ${tab === 'transfers' ? styles.tabActive : ''}`}
        >
          Token Transfers
        </a>
        <a
          href={`/token/${hash}?tab=holders`}
          className={`${styles.tab} ${tab === 'holders' ? styles.tabActive : ''}`}
        >
          Holders
        </a>
      </div>

      {/* Transfers Table */}
      {tab === 'transfers' && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Txn Hash</th>
                <th>Block</th>
                <th>Age</th>
                <th>From</th>
                <th>To</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !currentQuery.isLoading && (
                <tr>
                  <td colSpan={6}>
                    <div className={styles.emptyState}>
                      <FiInbox className={styles.emptyIcon} size={48} />
                      <p>No transfers found</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map((t: any, i: number) => (
                <tr key={`${t.transaction_hash}-${i}`}>
                  <td>
                    <a href={`/tx/${t.transaction_hash}`} className={styles.link}>
                      {t.transaction_hash?.slice(0, 10)}...{t.transaction_hash?.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <a href={`/block/${t.block_number}`} className={styles.link}>
                      {t.block_number}
                    </a>
                  </td>
                  <td style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem' }}>
                    {formatTimestamp(t.timestamp)}
                  </td>
                  <td>
                    <a href={`/address/${t.from?.hash}`} className={styles.link}>
                      {t.from?.hash?.slice(0, 8)}...{t.from?.hash?.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <a href={`/address/${t.to?.hash}`} className={styles.link}>
                      {t.to?.hash?.slice(0, 8)}...{t.to?.hash?.slice(-4)}
                    </a>
                  </td>
                  <td>{formatTokenAmount(t.total?.value, t.total?.decimals)} {token.symbol}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Holders Table */}
      {tab === 'holders' && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Address</th>
                <th>Quantity</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !currentQuery.isLoading && (
                <tr>
                  <td colSpan={4}>
                    <div className={styles.emptyState}>
                      <FiInbox className={styles.emptyIcon} size={48} />
                      <p>No holders found</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map((h: any, i: number) => {
                const pct = h.value && token.total_supply
                  ? BigNumber(h.value).dividedBy(BigNumber(token.total_supply)).multipliedBy(100).toFixed(2)
                  : '0';
                return (
                  <tr key={h.address?.hash || i}>
                    <td>{i + 1 + (currentQuery.pagination.page - 1) * 50}</td>
                    <td>
                      <a href={`/address/${h.address?.hash}`} className={styles.link} style={{ fontFamily: 'monospace' }}>
                        {h.address?.hash}
                      </a>
                    </td>
                    <td>{formatTokenAmount(h.value, token.decimals)} {token.symbol}</td>
                    <td>
                      <div className={styles.holderBar}>
                        <span>{pct}%</span>
                        <div className={styles.holderBarTrack}>
                          <div className={styles.holderBarFill} style={{ width: `${Math.min(Number(pct), 100)}%` }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {currentQuery.isLoading && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={`skel-${i}`}>
                  {[...Array(tab === 'holders' ? 4 : 6)].map((_, j) => (
                    <td key={j}><span className={styles.skeleton} style={{ width: j === 0 ? '140px' : '80px' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!currentQuery.isLoading && items.length > 0 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            disabled={!currentQuery.pagination.canGoBackwards}
            onClick={currentQuery.pagination.onPrevPageClick}
          >
            &lt;
          </button>
          <button className={`${styles.pageButton} ${styles.pageButtonActive}`}>
            {currentQuery.pagination.page}
          </button>
          <button
            className={styles.pageButton}
            disabled={!currentQuery.pagination.hasNextPage}
            onClick={currentQuery.pagination.onNextPageClick}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
};

function formatTokenAmount(value: string | null | undefined, decimals: string | null | undefined) {
  if (!value || value === '0') return '0';
  const dec = decimals ? Number(decimals) : 18;
  const bn = BigNumber(value);
  const amount = bn.dividedBy(BigNumber(10).pow(dec));
  if (amount.isGreaterThanOrEqualTo(1000000000)) return `${amount.dividedBy(1000000000).toFixed(2)}B`;
  if (amount.isGreaterThanOrEqualTo(1000000)) return `${amount.dividedBy(1000000).toFixed(2)}M`;
  if (amount.isGreaterThanOrEqualTo(1000)) return `${amount.dividedBy(1000).toFixed(2)}K`;
  return amount.toFixed(4);
}

export default TokenDetailPage;
